'use client'

import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { clsx } from 'clsx'
import { cva, VariantProps } from 'class-variance-authority'
import { CaretSortIcon, CheckIcon } from '@radix-ui/react-icons'

const defaultVariants = {
  color: 'black',
  size: 'small',
  iconRight: true,
  narrow: false,
} as const

const $trigger = cva(
  'flex text-left cursor-default items-center border rounded-lg focus:border-dark-control-base focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      color: {
        black:
          'placeholder:text-dark-muted text-dark-bright enabled:hover:text-dark-dim bg-dark-shade border-dark-border-solid',
        orange:
          'bg-dark-control-base border-dark-control-highlight enabled:hover:bg-dark-control-highlight',
        red: 'bg-red text-dark-bright ',
        transparent:
          'border-transparent text-dark-dim enabled:hover:text-dark-bright [&>svg]:-mb-px',
      },
      size: {
        small: 'text-xs h-7',
        medium: 'text-sm',
        large: 'text-base',
      },
      narrow: {
        true: 'gap-0.5 px-1',
        false: 'gap-1 px-2',
      },
      iconRight: {
        true: 'flex-row-reverse [&>:not(svg)]:grow [&>span]:truncate',
      },
    },
    defaultVariants,
  }
)

const $caret = cva('text-dark-muted shrink-0', {
  variants: {
    size: {
      small: 'h-3.5 w-3.5',
      medium: 'h-4 w-4',
      large: 'h-5 w-5',
    },
  },
  defaultVariants,
})

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
    VariantProps<typeof $trigger> & {
      noCaret?: boolean
      caretClassName?: string
      unstyled?: boolean
    }
>(
  (
    {
      className,
      size,
      color,
      children,
      iconRight,
      noCaret,
      caretClassName,
      unstyled,
      narrow,
      ...props
    },
    ref
  ) => {
    return unstyled ? (
      <SelectPrimitive.Trigger ref={ref} className={className} {...props}>
        {!noCaret && (
          <CaretSortIcon
            className={$caret({ size, className: caretClassName })}
          />
        )}
        {children}
      </SelectPrimitive.Trigger>
    ) : (
      <SelectPrimitive.Trigger
        ref={ref}
        className={$trigger({ size, color, className, iconRight, narrow })}
        {...props}
      >
        {!noCaret && (
          <CaretSortIcon
            className={$caret({ size, className: caretClassName })}
          />
        )}
        {children}
      </SelectPrimitive.Trigger>
    )
  }
)

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={clsx(
        'shadow-md relative z-[110] overflow-hidden rounded-lg border border-dark-border-solid bg-dark-base shadow-modal animate-in fade-in-80',
        className
      )}
      {...props}
    >
      <SelectPrimitive.Viewport className="p-1">
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={clsx(
      'py-1.5 px-8 text-sm font-semibold text-dark-bright',
      className
    )}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    indicator?: React.ReactNode
    afterText?: React.ReactNode
  }
>(({ className, children, indicator, afterText, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={clsx(
      'relative flex select-none items-center rounded py-1 pr-1 pl-6 text-xs text-dark-dim outline-none focus:bg-dark-shade data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className
    )}
    {...props}
  >
    {indicator ?? (
      <span className="absolute left-1.5 flex h-3 w-3 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-[15px]" />
        </SelectPrimitive.ItemIndicator>
      </span>
    )}

    <SelectPrimitive.ItemText className="select-none flex-1">
      {children}
    </SelectPrimitive.ItemText>

    {afterText}
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={clsx('-mx-1 my-1 h-px bg-dark-border-solid', className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
}
